import { LogUserViewTrack } from "@/@types/app/LogUserViewTrack";
import { appApi, genericApi } from "../api";
import { TrackFile } from "@/@types/app/TrackFile";
import { calculatePercentage } from "@/utils/calculatePercentage";

export type TrackFileFormData = {
	title: string;
	description: string;
	urlVideo: string;
	videoFile: File;
	thumbnailFile: File;
	imageFile?: File;
	duration: number;
};

export type UploadFileUrl = {
	uploadUrl: string;
};

export async function uploadVideoWithProgress(
	file: File,
	sasUrl: string,
	onProgress: (percent: number) => void
): Promise<boolean> {
	const response = await genericApi.put(sasUrl, file, {
		headers: {
			"x-ms-blob-type": "BlockBlob",
			"Content-Type": file.type,
			Authorization: undefined,
		},
		transformRequest: [
			(data, headers) => {
				delete headers["Authorization"];
				return data;
			},
		],
		onUploadProgress: (progressEvent) => {
			if (progressEvent.total) {
				const percentComplete = calculatePercentage(
					progressEvent.loaded,
					progressEvent.total
				);
				onProgress(percentComplete);
			}
		},
	});
	return response.status === 201;
}

export async function getUrlUploadToAzure(fileName: string) {
	const response = await appApi.get<UploadFileUrl>("/TrackFile/GenerateUploadSasToken", {
		fileName,
	});
	return response;
}

export async function registerTrackFile(trackFileFormData: TrackFileFormData) {
	if (!trackFileFormData.thumbnailFile) return {} as TrackFile;
	const formData = new FormData();
	formData.append("thumbnailFile", trackFileFormData.thumbnailFile);
	if (trackFileFormData.imageFile) {
		formData.append("imageFile", trackFileFormData.imageFile);
	}
	formData.append("title", trackFileFormData.title);
	formData.append("description", trackFileFormData.description);
	formData.append("duration", String(Math.floor(trackFileFormData.duration)));
	formData.append("urlVideo", trackFileFormData.urlVideo);
	const { data } = await appApi.post<TrackFile>("/trackfile/registerFile", formData, {
		headers: {
			"Content-Type": "multipart/form-data",
		},
	});
	return data || ({} as TrackFile);
}

export async function updateTrackFileImage(
	trackFileId: number,
	trackFileFormData: TrackFileFormData
) {
	if (!trackFileFormData.thumbnailFile && !trackFileFormData.imageFile) return;
	const formData = new FormData();
	if (trackFileFormData.thumbnailFile) {
		formData.append("thumbnailFile", trackFileFormData.thumbnailFile);
	}
	if (trackFileFormData.imageFile) {
		formData.append("imageFile", trackFileFormData.imageFile);
	}
	formData.append("id", String(trackFileId));
	const response = await appApi.post("/trackfile/updateimages", formData, {
		headers: {
			"Content-Type": "multipart/form-data",
		},
	});
	return response.status === 200;
}

export async function updateTrackFile(trackFile: TrackFile) {
	const response = await appApi.post("/TrackFile/Update", trackFile);
	return response.status === 200;
}

export async function getCurrentLogUserViewTrack(trackOrderId: number) {
	const { data } = await appApi.get<LogUserViewTrack>(
		`/LogUserViewTrack/GetCurrentLogByTrackOrderId`,
		{
			trackOrderId,
		}
	);
	return data || ({} as LogUserViewTrack);
}

export async function updateTrackProgress(trackOrderId: number, playedSeconds: number) {
	const response = await appApi.post("/LogUserViewTrack/register", {
		trackOrderId,
		currentTimeTrack: Math.floor(playedSeconds),
	});
	return response.status === 200;
}

export async function getVideoUrl(trackFileId: number) {
	const { data } = await appApi.get<string>("/trackfile/GetUrlVideo", { trackFileId });
	return data || "";
}
